/**
 * Process detail → Approvals tab. Shows per-level progress (tech_lead →
 * storekeeper: done / pending), the recorded-approval `Timeline`, and — when the
 * process is still actionable — the `ApprovalActions` decision panel.
 */
import { Check } from 'lucide-react';
import { Timeline, EmptyState, StatusPill, type TimelineItem } from '@vnetwork/ui/data';
import { formatDateTime } from '@/lib/format';
import type { ApproverRole, ProcessApproval, ProcessStatus, ProcessType } from '@/api/capacities';
import { APPROVER_ROLE_LABEL } from '../process-format';
import { ApprovalActions, isRejection } from './approval-actions';

/** The two approval levels, in order. */
const ROLES: ApproverRole[] = ['tech_lead', 'storekeeper'];

const roleLabel = (approval: ProcessApproval): string =>
  approval.approver_role
    ? APPROVER_ROLE_LABEL[approval.approver_role] ?? approval.approver_role
    : 'Approval';

/** Actor line: "Name · Department" (department read from the loose approval shape). */
function actorMeta(approval: ProcessApproval): string {
  const dept = (approval as { approver_department?: string }).approver_department;
  return [approval.approver_name, dept].filter(Boolean).join(' · ') || '—';
}

/** Per-level chips: which of tech_lead / storekeeper have approved vs pending. */
function ApprovalProgress({ approvals }: { approvals: ProcessApproval[] }) {
  const done = new Set(approvals.filter((a) => !isRejection(a.action)).map((a) => a.approver_role));
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-2)' }}>
      {ROLES.map((r) => (
        <StatusPill key={r} tone={done.has(r) ? 'success' : 'warning'}>
          {APPROVER_ROLE_LABEL[r]}: {done.has(r) ? 'Approved' : 'Pending'}
        </StatusPill>
      ))}
    </div>
  );
}

export function ProcessApprovalsTab({
  processId,
  type,
  status,
  approvals,
}: {
  processId: string;
  type: ProcessType;
  status: ProcessStatus;
  approvals: ProcessApproval[];
}) {
  const items: TimelineItem[] = approvals.map((a, i) => ({
    id: a.id ?? i,
    title: roleLabel(a),
    meta: actorMeta(a),
    description: [a.action, a.comments].filter(Boolean).join(' — ') || undefined,
    timestamp: a.created_at ? formatDateTime(a.created_at) : undefined,
    tone: isRejection(a.action) ? 'danger' : 'success',
    icon: <Check size={12} />,
  }));

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <ApprovalProgress approvals={approvals} />

      {approvals.length === 0 ? (
        <EmptyState
          icon={<Check size={24} />}
          title="No approvals yet"
          description="This process has not been through the approval flow."
        />
      ) : (
        <Timeline items={items} />
      )}

      <ApprovalActions processId={processId} type={type} status={status} approvals={approvals} />
    </div>
  );
}
